import { Fragment, useState, useRef, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import ReactQuill from 'react-quill';
import { EmailTextBlock } from '../services/emailTextBlocks.service';
import { uploadEmailAsset } from '../lib/storage';
import { useAuth } from '../contexts/AuthContext';

// Quill toolbar configuration
const quillModules = {
  toolbar: [
    ['bold', 'italic', 'underline'],
    [{ 'list': 'bullet' }],
    [{ 'align': [] }],
    ['link'],
    ['clean']
  ]
};

const quillFormats = [
  'bold', 'italic', 'underline',
  'list', 'bullet',
  'align',
  'link'
];

/**
 * Field order for the initial email (type 1) and the email with codes (type 2).
 * Mirrors the order the blocks appear in those templates.
 */
const DEFAULT_FIELD_ORDER = [
  'preheader_text',
  'url',
  'logo',
  'intro',
  'hi_hello',
  'color',
  'header1',
  'image',
  'location_address',
  'generic_block1',
  'term_for_gate',
  'valid_from',
  'valid_until',
  'generic_block2',
  'header2',
  'generic_block3',
  'codes_placeholder',
  'generic_block4',
  'header3',
  'generic_block5',
  'header4',
  'generic_block6',
  'extend_order_text',
  'extend_order_button',
  'header5',
  'generic_block7',
  'take_picture_text',
  'take_picture_button',
  'generic_block8',
  'cancel_booking_text',
  'cancel_booking_button',
  'download_receipt_text',
  'download_terms_text',
  'subject',
  'location_name'
] as const;

/**
 * Field order for the delivery email (type 3).
 *
 * The delivery template only renders headers 1-3, generic blocks 1-3, the extend
 * and cancel buttons and the two footer downloads. Everything else (image,
 * location address, valid from/until, blocks 4-8, headers 4-5, take picture) has
 * no placeholder in that template, so it is not offered for editing here.
 * Start and end date come from the booking ({{startdate}} / {{enddate}}), not
 * from valid_from / valid_until.
 */
const DELIVERY_FIELD_ORDER = [
  'preheader_text',
  'url',
  'logo',
  'color',
  'hi_hello',
  'intro',
  'header1',
  'generic_block1',
  'header2',
  'generic_block2',
  'header3',
  'generic_block3',
  'extend_order_text',
  'extend_order_button',
  'cancel_booking_text',
  'cancel_booking_button',
  'download_receipt_text',
  'download_terms_text',
  'subject',
  'location_name'
] as const;

interface EmailFormFieldsProps {
  formData: Partial<EmailTextBlock>;
  updateField: (field: keyof EmailTextBlock, value: any) => void;
  emailType: 1 | 2 | 3;
}

export default function EmailFormFields({ formData, updateField, emailType }: EmailFormFieldsProps) {
  const { t } = useTranslation();
  const { merchantId } = useAuth();
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const logoInputRef = useRef<HTMLInputElement>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);

  const handleFileUpload = async (
    file: File,
    fieldName: 'logo' | 'image',
    assetType: 'logo' | 'image'
  ) => {
    if (!merchantId) {
      setUploadError(t('emailFormFields.merchantIdNotFound'));
      return;
    }

    try {
      setUploadError(null);
      if (fieldName === 'logo') {
        setUploadingLogo(true);
      } else {
        setUploadingImage(true);
      }

      const publicUrl = await uploadEmailAsset(file, merchantId, assetType);
      updateField(fieldName, publicUrl);
    } catch (error) {
      console.error('Upload error:', error);
      setUploadError(error instanceof Error ? error.message : t('emailFormFields.failedToUpload'));
    } finally {
      if (fieldName === 'logo') {
        setUploadingLogo(false);
      } else {
        setUploadingImage(false);
      }
      // Reset file input
      if (fieldName === 'logo' && logoInputRef.current) {
        logoInputRef.current.value = '';
      } else if (fieldName === 'image' && imageInputRef.current) {
        imageInputRef.current.value = '';
      }
    }
  };

  const textField = (
    field: keyof EmailTextBlock,
    labelKey: string,
    options: { type?: string; maxLength?: number; placeholder?: string; showCounter?: boolean } = {}
  ) => (
    <div className="form-group">
      <label className="form-label">{t(labelKey)}</label>
      <input
        type={options.type || 'text'}
        maxLength={options.maxLength}
        value={(formData[field] as string) || ''}
        onChange={(e) => updateField(field, e.target.value)}
        className="input w-full"
        placeholder={options.placeholder}
      />
      {options.showCounter && options.maxLength && (
        <span className="text-xs text-secondary">
          {((formData[field] as string) || '').length}/{options.maxLength}
        </span>
      )}
    </div>
  );

  const quillField = (
    field: keyof EmailTextBlock,
    labelKey: string,
    placeholderKey = 'emailFormFields.enterContent'
  ) => (
    <div className="form-group col-span-full">
      <label className="form-label">{t(labelKey)}</label>
      <ReactQuill
        value={(formData[field] as string) || ''}
        onChange={(value) => updateField(field, value)}
        modules={quillModules}
        formats={quillFormats}
        theme="snow"
        placeholder={t(placeholderKey)}
      />
    </div>
  );

  const uploadField = (
    field: 'logo' | 'image',
    labelKey: string,
    placeholderKey: string,
    previewAltKey: string,
    previewClassName: string
  ) => {
    const inputRef = field === 'logo' ? logoInputRef : imageInputRef;
    const uploading = field === 'logo' ? uploadingLogo : uploadingImage;

    return (
      <div className="form-group">
        <label className="form-label">{t(labelKey)}</label>
        <div className="flex gap-2">
          <input
            type="text"
            value={formData[field] || ''}
            onChange={(e) => updateField(field, e.target.value)}
            className="input flex-1"
            placeholder={t(placeholderKey)}
          />
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            style={{ display: 'none' }}
            onChange={(e) => {
              e.stopPropagation();
              const file = e.target.files?.[0];
              if (file) {
                handleFileUpload(file, field, field);
              }
            }}
          />
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              inputRef.current?.click();
            }}
            disabled={uploading}
            className="btn btn-secondary whitespace-nowrap"
          >
            {uploading ? t('emailFormFields.uploading') : t('emailFormFields.upload')}
          </button>
        </div>
        {formData[field] && (
          <div className="mt-2">
            <img src={formData[field] as string} alt={t(previewAltKey)} className={previewClassName} />
          </div>
        )}
      </div>
    );
  };

  const fields: Record<string, ReactNode> = {
    preheader_text: textField('preheader_text', 'emailFormFields.preheaderText', {
      maxLength: 140,
      placeholder: t('emailFormFields.max140Chars'),
      showCounter: true
    }),

    url: textField('url', 'emailFormFields.url', { type: 'url', placeholder: 'https://...' }),

    logo: uploadField(
      'logo',
      'emailFormFields.logo',
      'emailFormFields.logoUrlOrUpload',
      'emailFormFields.logoPreview',
      'w-24 h-12 object-contain border rounded'
    ),

    intro: textField('intro', 'emailFormFields.intro'),

    hi_hello: textField('hi_hello', 'emailFormFields.hiHello', {
      maxLength: 20,
      placeholder: t('emailFormFields.max20Chars'),
      showCounter: true
    }),

    color: (
      <div className="form-group">
        <label className="form-label">{t('emailFormFields.colorHex')}</label>
        <div className="flex items-center gap-sm">
          <input
            type="color"
            value={formData.color || '#000000'}
            onChange={(e) => updateField('color', e.target.value)}
            className="w-12 h-10 border rounded cursor-pointer"
          />
          <input
            type="text"
            value={formData.color || '#000000'}
            onChange={(e) => updateField('color', e.target.value)}
            className="input flex-1"
            placeholder="#000000"
          />
        </div>
      </div>
    ),

    header1: textField('header1', 'emailFormFields.header1'),

    image: uploadField(
      'image',
      'emailFormFields.image',
      'emailFormFields.imageUrlOrUpload',
      'emailFormFields.imagePreview',
      'w-40 h-24 object-contain border rounded'
    ),

    location_address: quillField(
      'location_address',
      'emailFormFields.locationAddress',
      'emailFormFields.enterLocationAddress'
    ),

    generic_block1: quillField('generic_block1', 'emailFormFields.genericBlock1'),

    term_for_gate: textField('term_for_gate', 'emailFormFields.termForGate'),

    valid_from: textField('valid_from', 'emailFormFields.validFrom'),

    valid_until: textField('valid_until', 'emailFormFields.validUntil'),

    generic_block2: quillField('generic_block2', 'emailFormFields.genericBlock2'),

    header2: textField('header2', 'emailFormFields.header2'),

    generic_block3: quillField('generic_block3', 'emailFormFields.genericBlock3'),

    codes_placeholder: (
      <div className="form-group col-span-full">
        <div
          style={{
            border: '2px dashed #94a3b8',
            borderRadius: '8px',
            padding: '16px 20px',
            backgroundColor: '#f8fafc',
            textAlign: 'center',
            color: '#64748b',
            fontSize: '14px',
            fontStyle: 'italic',
          }}
        >
          {t('emailFormFields.codesPlaceholder')}
        </div>
      </div>
    ),

    generic_block4: quillField('generic_block4', 'emailFormFields.genericBlock4'),

    header3: textField('header3', 'emailFormFields.header3'),

    generic_block5: quillField('generic_block5', 'emailFormFields.genericBlock5'),

    header4: textField('header4', 'emailFormFields.header4'),

    generic_block6: quillField('generic_block6', 'emailFormFields.genericBlock6'),

    extend_order_text: quillField('extend_order_text', 'emailFormFields.extendOrderText'),

    extend_order_button: textField('extend_order_button', 'emailFormFields.extendOrderButton'),

    header5: textField('header5', 'emailFormFields.header5'),

    generic_block7: quillField('generic_block7', 'emailFormFields.genericBlock7'),

    take_picture_text: quillField('take_picture_text', 'emailFormFields.takePictureText'),

    take_picture_button: textField('take_picture_button', 'emailFormFields.takePictureButton'),

    generic_block8: quillField('generic_block8', 'emailFormFields.genericBlock8'),

    cancel_booking_text: quillField('cancel_booking_text', 'emailFormFields.cancelBookingText'),

    cancel_booking_button: textField('cancel_booking_button', 'emailFormFields.cancelBookingButton'),

    download_receipt_text: textField('download_receipt_text', 'emailFormFields.downloadReceiptText'),

    download_terms_text: textField('download_terms_text', 'emailFormFields.downloadTermsText'),

    subject: textField('subject', 'emailFormFields.subject'),

    location_name: textField('location_name', 'emailFormFields.locationName')
  };

  // Term for gate and the codes placeholder only exist in the email with codes (type 2)
  const fieldOrder = (emailType === 3
    ? DELIVERY_FIELD_ORDER
    : DEFAULT_FIELD_ORDER.filter(
        field => emailType === 2 || (field !== 'term_for_gate' && field !== 'codes_placeholder')
      )
  ) as readonly string[];

  return (
    <div className="form-grid">
      {/* Upload Error Display */}
      {uploadError && (
        <div className="col-span-full bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {uploadError}
        </div>
      )}

      {fieldOrder.map(field => (
        <Fragment key={field}>{fields[field]}</Fragment>
      ))}
    </div>
  );
}
